/**
 * New Lead Form
 * Create a new lead in the sales pipeline
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  Stack,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { MdArrowBack, MdSave } from 'react-icons/md';
import { useAuth } from '../../../contexts/AuthContext';
import { createLead } from '../../../services/leadManagement';
import { LeadSource, LeadCreateData } from '../../../types/lead';

export function NewLead() {
  const navigate = useNavigate();
  const toast = useToast();
  const { userProfile } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<Partial<LeadCreateData>>({
    source: 'website',
    contactInfo: {
      name: '',
      email: '',
      phone: '',
      company: '',
      position: '',
    },
    businessInfo: {
      doorCount: 0,
      city: '',
      province: 'ON',
      currentSolution: '',
      painPoints: [],
      budget: '',
      timeline: '',
    },
  });

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleContactChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo!,
        [field]: value,
      },
    }));
  };

  const handleBusinessChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      businessInfo: {
        ...prev.businessInfo!,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userProfile || !userProfile.name) {
      toast({
        title: 'Error',
        description: 'User profile not loaded',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!formData.contactInfo?.name || !formData.contactInfo?.email || !formData.businessInfo?.doorCount) {
      toast({
        title: 'Required fields missing',
        description: 'Please fill in name, email, and door count',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      setSubmitting(true);
      const newLead = await createLead(
        formData as LeadCreateData,
        userProfile.uid
      );
      
      toast({
        title: 'Lead created',
        description: `${formData.contactInfo.name} has been added to the pipeline`,
        status: 'success',
        duration: 3000,
      });
      
      navigate(`/admin/sales/leads/${newLead.id}`);
    } catch (error) {
      toast({
        title: 'Error creating lead',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <HStack mb="6">
        <IconButton
          aria-label="Back"
          icon={<MdArrowBack />}
          onClick={() => navigate('/admin/sales/leads')}
        />
        <Heading size="lg">New Lead</Heading>
      </HStack>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <Heading size="md">Lead Information</Heading>
          </CardHeader>
          <CardBody>
            <Stack spacing="6">
              {/* Contact Information */}
              <Box>
                <Heading size="sm" mb="4">Contact Information</Heading>
                <Grid templateColumns="repeat(12, 1fr)" gap="4">
                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl isRequired>
                      <FormLabel>Full Name</FormLabel>
                      <Input
                        value={formData.contactInfo?.name || ''}
                        onChange={(e) => handleContactChange('name', e.target.value)}
                        placeholder="John Doe"
                      />
                    </FormControl>
                  </GridItem>
                  
                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl isRequired>
                      <FormLabel>Email</FormLabel>
                      <Input
                        type="email"
                        value={formData.contactInfo?.email || ''}
                        onChange={(e) => handleContactChange('email', e.target.value)}
                        placeholder="john@example.com"
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl>
                      <FormLabel>Phone</FormLabel>
                      <Input
                        type="tel"
                        value={formData.contactInfo?.phone || ''}
                        onChange={(e) => handleContactChange('phone', e.target.value)}
                        placeholder="+1 (555) 123-4567"
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl>
                      <FormLabel>Company</FormLabel>
                      <Input
                        value={formData.contactInfo?.company || ''}
                        onChange={(e) => handleContactChange('company', e.target.value)}
                        placeholder="Property Management Inc."
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl>
                      <FormLabel>Position/Title</FormLabel>
                      <Input
                        value={formData.contactInfo?.position || ''}
                        onChange={(e) => handleContactChange('position', e.target.value)}
                        placeholder="Property Manager"
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>

              {/* Business Information */}
              <Box>
                <Heading size="sm" mb="4">Business Information</Heading>
                <Grid templateColumns="repeat(12, 1fr)" gap="4">
                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl isRequired>
                      <FormLabel>Door Count (Number of Rental Units)</FormLabel>
                      <Input
                        type="number"
                        min="1"
                        value={formData.businessInfo?.doorCount || ''}
                        onChange={(e) => handleBusinessChange('doorCount', parseInt(e.target.value) || 0)}
                        placeholder="50"
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl>
                      <FormLabel>Province</FormLabel>
                      <Select
                        value={formData.businessInfo?.province || 'ON'}
                        onChange={(e) => handleBusinessChange('province', e.target.value)}
                      >
                        <option value="ON">Ontario</option>
                        <option value="QC">Quebec</option>
                        <option value="BC">British Columbia</option>
                        <option value="AB">Alberta</option>
                        <option value="MB">Manitoba</option>
                        <option value="SK">Saskatchewan</option>
                        <option value="NS">Nova Scotia</option>
                        <option value="NB">New Brunswick</option>
                        <option value="NL">Newfoundland and Labrador</option>
                        <option value="PE">Prince Edward Island</option>
                      </Select>
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl>
                      <FormLabel>City</FormLabel>
                      <Input
                        value={formData.businessInfo?.city || ''}
                        onChange={(e) => handleBusinessChange('city', e.target.value)}
                        placeholder="Toronto"
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl>
                      <FormLabel>Current Solution</FormLabel>
                      <Input
                        value={formData.businessInfo?.currentSolution || ''}
                        onChange={(e) => handleBusinessChange('currentSolution', e.target.value)}
                        placeholder="Manual tracking / Spreadsheets"
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl>
                      <FormLabel>Budget Indication</FormLabel>
                      <Select
                        value={formData.businessInfo?.budget || ''}
                        onChange={(e) => handleBusinessChange('budget', e.target.value)}
                      >
                        <option value="">Not specified</option>
                        <option value="under_1k">Under $1,000/year</option>
                        <option value="1k_3k">$1,000 - $3,000/year</option>
                        <option value="3k_5k">$3,000 - $5,000/year</option>
                        <option value="5k_10k">$5,000 - $10,000/year</option>
                        <option value="over_10k">Over $10,000/year</option>
                      </Select>
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={{ base: 12, md: 6 }}>
                    <FormControl>
                      <FormLabel>Timeline</FormLabel>
                      <Select
                        value={formData.businessInfo?.timeline || ''}
                        onChange={(e) => handleBusinessChange('timeline', e.target.value)}
                      >
                        <option value="">Not specified</option>
                        <option value="urgent">Urgent (ASAP)</option>
                        <option value="1_month">Within 1 month</option>
                        <option value="1_3_months">1-3 months</option>
                        <option value="3_6_months">3-6 months</option>
                        <option value="exploring">Just exploring</option>
                      </Select>
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={12}>
                    <FormControl>
                      <FormLabel>Pain Points / Notes</FormLabel>
                      <Textarea
                        value={formData.businessInfo?.painPoints?.join('\n') || ''}
                        onChange={(e) => handleBusinessChange('painPoints', e.target.value.split('\n').filter(Boolean))}
                        placeholder="Enter pain points (one per line)&#10;Example: Manual rent collection takes too much time&#10;Late payments are hard to track&#10;Need better reporting"
                        rows={4}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>

              {/* Lead Source */}
              <Box>
                <Heading size="sm" mb="4">Lead Source</Heading>
                <FormControl isRequired>
                  <FormLabel>How did they find us?</FormLabel>
                  <Select
                    value={formData.source || 'website'}
                    onChange={(e) => handleChange('source', e.target.value as LeadSource)}
                  >
                    <option value="website">Website Demo Request</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="referral">Referral</option>
                    <option value="cold_outreach">Cold Outreach</option>
                    <option value="paid_ad">Paid Advertising</option>
                    <option value="other">Other</option>
                  </Select>
                </FormControl>
              </Box>

              {/* Actions */}
              <HStack justify="flex-end" pt="4" borderTop="1px solid" borderColor="gray.200">
                <Button onClick={() => navigate('/admin/sales/leads')}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="brand"
                  leftIcon={<MdSave />}
                  isLoading={submitting}
                >
                  Create Lead
                </Button>
              </HStack>
            </Stack>
          </CardBody>
        </Card>
      </form>
    </Box>
  );
}
